import { switchMap, tap } from 'rxjs/operators';
import {
    Component,
    Inject,
    ViewChild,
    AfterViewInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ElementRef,
    signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Animation } from '../animation';
import { StateService } from '../../controller/state.svc';
import { Observable, Subscription, BehaviorSubject, EMPTY, timer } from 'rxjs';
import { LoadingIndicatorState } from '../ui-states';
import { AnimationOptions } from 'ngx-lottie';

/**
 * This is the main loader component.
 *
 * @author
 * @since 2018-02-18
 */

@Component({
    selector: 'main-loader',
    templateUrl: 'main-loader.html',
    styleUrls: [ 'main-loader.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLoader implements AfterViewInit, OnDestroy {

    /**
     * A behavior subject that emits boolean values to show
     * and hide the loader.
     */
    public showLoader: BehaviorSubject<boolean>;

    /**
     * Spinner element.
     */
    @ViewChild( 'spinner' )
    protected spinner: ElementRef<HTMLDivElement>;

    /**
     * Optional description to show below loading indicator.
     */
    public description = signal<string | null>(null);

    public showLogo = signal<boolean>(false);

    /**
     * Logo element.
     */
    @ViewChild( 'logo' )
    protected logo: ElementRef<HTMLDivElement>;

    /**
     * Logo Animation element.
     */
    @ViewChild('logoAnimation', { read: ElementRef })
    protected logoAnimation: ElementRef<HTMLElement>;

    /**
     * Loader element.
     */
    @ViewChild( 'loader' )
    protected loader: ElementRef<HTMLDivElement>;

    /**
     * Description element (for animating text changes)
     */
    @ViewChild('desc', { read: ElementRef })
    protected desc: ElementRef<HTMLElement>;

    /**
     * Subscriptions
     */
    protected subs: Subscription[] = [];

    /**
     * Subscription for rotating fallback descriptions.
     */
    protected descCycleSub: Subscription;

    /**
     * Fallback messages when a description isn't provided by state.
     * Loaded from i18n at runtime. Kept empty by default.
     */
    protected fallbackMessages: string[] = [];
    protected fallbackIndex = 0;

    /**
     * Lottie animation configuration for the logo animation.
     */
    public logoAnimationConfig: AnimationOptions = {
        path: './assets/anim/logo_animation.json',
        autoplay: true,
        loop: true,
    };

    /**
     * Constructor
     */
    constructor(
        @Inject( StateService ) protected state: LoadingIndicatorState,
        protected changeDetectorRef: ChangeDetectorRef,
        protected translate: TranslateService,
    ) {
        this.showLoader = new BehaviorSubject( true );
    }

    /**
     * Checks the LoadingIndicator state and shows the
     * indicator if the state data is true, and hides the
     * indicator if the state data is false.
     */
    public ngAfterViewInit() {
        const userLang = localStorage.getItem('user_language');

        // If user has a stored language, use it immediately
        if (userLang) {
            this.translate.use(userLang);
        }

        // Subscribe to i18n stream for future language changes
        const i18nSub = this.translate
        .stream('MAIN_LOADER.MESSAGES')
        .subscribe((msgs: any) => {
            if ( Array.isArray(msgs) && msgs.length ) {
                this.fallbackMessages = msgs as string[];
            }
            // Reset index so we start from the first message on next cycle
            this.fallbackIndex = 0;
        });
        this.subs.push(i18nSub);

        const indicatorSub = this.state.changes( 'LoadingIndicatorState' ).pipe(
            switchMap( data => {
                if (data.description) {
                    this.description.set(data.description);
                }
                if ( data.main ) {
                    return this.show(data);
                }
                return this.hide();
            })).subscribe();
        this.subs.push( indicatorSub );
    }

    /**
     * Sets the showLoader to true and runs the animations to
     * fade in the logo and cycle through the loading messages.
     */
    public show(data: any) {
        this.showLoader.next( true );
        // this.showLoader is updated async so run change detection
        // before attempting to access elements to prevent null reference errors
        this.changeDetectorRef.detectChanges();
        const hasDesc = !!data.description;
        // If data has a description, show it and stop any cycling
        if ( hasDesc ) {
            this.stopDescriptionCycle();
            this.description.set( data.description );
        }

        if ( data.staticLogo ) {
            this.showLogo.set(true);

            return this.fadeIn( this.logo.nativeElement ).start().pipe(
                switchMap(() => this.moveUpwards( this.logo.nativeElement ).start()),
                tap(() => this.spinner.nativeElement.classList.remove( 'paused' )),
                switchMap(() => this.fadeIn( this.spinner.nativeElement ).start()),
            );
        }

        return this.fadeIn(this.logoAnimation.nativeElement).start().pipe(
            tap(() => {
                if (!hasDesc) {
                    setTimeout(() => this.startDescriptionCycle(), 3000);
                }
            })
        );
    }

    /**
     * Sets the showLoader to false and returns an empty observable.
     */
    public hide(): Observable<any> {
        // Note:  When the window.location changes loader won't be available
        // e.g when Logout
        if ( !this.loader ) {
            return EMPTY;
        }
        this.stopDescriptionCycle();
        return this.fadeOut( this.loader.nativeElement ).start().pipe(
            tap({
                complete: () => this.showLoader.next( false ),
            }),
        );
    }

    /**
     * Unsubscribe from all subscriptions.
     */
    ngOnDestroy() {
        this.stopDescriptionCycle();
        while ( this.subs.length > 0 ) {
            this.subs.pop().unsubscribe();
        }
    }

    /**
     * Fade out animation.
     */
    protected fadeOut( elem: HTMLElement ): Animation {
        const transition = new Animation({
            from: { opacity: '1' },
            to: { opacity: '0' },
            transitionProperty: 'opacity',
            duration: 500,
            easing: 'linear',
        });
        transition.element = elem;
        return transition;
    }

    /**
     * Fade in animation.
     */
    protected fadeIn( elem: HTMLElement ): Animation {
        const transition = new Animation({
            from: { opacity: '0' },
            to: { opacity: '1' },
            transitionProperty: 'opacity',
            duration: 1500,
            easing: 'linear',
        });
        transition.element = elem;
        return transition;
    }

    /**
     * Move the element upwards by 20px.
     */
    protected moveUpwards( elem: HTMLElement ): Animation {
        const transition = new Animation({
            from: { transform: 'translateY(0%)' },
            to: { transform: 'translateY(-20px)' },
            transitionProperty: 'transform',
            duration: 500,
            easing: 'ease-in-out',
        });
        transition.element = elem;
        return transition;
    }

    /**
     * Fade out and move element slightly upwards.
     */
    protected fadeOutUp( elem: HTMLElement ): Animation {
        const transition = new Animation({
            from: { opacity: '1', transform: 'translateY(0px)' },
            to: { opacity: '0', transform: 'translateY(-10px)' },
            transitionProperty: 'opacity, transform',
            duration: 300,
            easing: 'ease-out',
        });
        transition.element = elem;
        return transition;
    }

    /**
     * Fade in and move element from slightly below.
     */
    protected fadeInDown( elem: HTMLElement ): Animation {
        const transition = new Animation({
            from: { opacity: '0', transform: 'translateY(10px)' },
            to: { opacity: '1', transform: 'translateY(0px)' },
            transitionProperty: 'opacity, transform',
            duration: 300,
            easing: 'ease-out',
        });
        transition.element = elem;
        return transition;
    }

    /**
     * Begin cycling fallback description messages with transitions.
     */
    protected startDescriptionCycle() {
        if ( this.descCycleSub ) { return; }
        this.fallbackIndex = 0;
        this.description.set( this.fallbackMessages[this.fallbackIndex] );
        this.changeDetectorRef.detectChanges();
        const elem = this.desc?.nativeElement;
        if ( !elem ) { return; }
        this.descCycleSub = timer( 4000, 4000 ).pipe(
            switchMap(() => this.fadeOutUp( elem ).start()),
            tap(() => {
                this.fallbackIndex = ( this.fallbackIndex + 1 ) % this.fallbackMessages.length;
                this.description.set( this.fallbackMessages[this.fallbackIndex] );
            }),
            switchMap(() => this.fadeInDown( elem ).start()),
        ).subscribe();
    }

    /**
     * Stop cycling messages and clear subscription.
     */
    protected stopDescriptionCycle() {
        if ( this.descCycleSub ) {
            this.descCycleSub.unsubscribe();
            this.descCycleSub = null;
        }
    }
}
